import { useFrame } from '@react-three/fiber';
import { useEffect } from 'react';
import { AVATURN_ANIMATED_FACE_MORPH_NAMES } from '../constants/emotionBlendshapes';
import { CONVAI_VISEME_BLENDSHAPES } from '../constants/viseme15Blendshapes';
import { getFacialFrameRate } from '../helpers/lipsync';
import { lerpMorphTarget } from '../helpers/utils';
import * as THREE from 'three';

/** Convai Classic lipsync from indexed viseme frames synced to audio playback. */
export const useModelLipsync = ({ client, scene, enabled = true }) => {
  useFrame(() => {
    if (!enabled) return;
    if (!client?.facialData || !client?.audioRef?.current) return;

    const currentTime = client.audioRef.current.currentTime;
    if (currentTime === 0) return;

    const exactFrame = currentTime * getFacialFrameRate(client);
    const prevFrameIndex = Math.floor(exactFrame);
    const nextFrameIndex = Math.ceil(exactFrame);
    const lerpFactor = exactFrame - prevFrameIndex;

    const prevFrame = client.facialData[prevFrameIndex];
    const nextFrame = client.facialData[nextFrameIndex];
    if (!prevFrame || !nextFrame) return;

    for (let idx = 0; idx < prevFrame.length; idx++) {
      const prevVal = prevFrame[idx];
      const nextVal = nextFrame[idx];
      const blendName = CONVAI_VISEME_BLENDSHAPES[idx];
      if (blendName && prevVal != null && nextVal != null) {
        const interpolatedValue = prevVal + (nextVal - prevVal) * lerpFactor;

        lerpMorphTarget({
          targetBlendShape: blendName,
          value: interpolatedValue,
          speed: 0.9,
          scene
        });
      }
    }
  });

  // Reset blendshapes when not talking
  useEffect(() => {
    if (!enabled || !scene) return;

    if (!client?.isTalking) {
      scene.traverse((child) => {
        if (child.isSkinnedMesh && child.morphTargetDictionary) {
          for (const target in child.morphTargetDictionary) {
            if (AVATURN_ANIMATED_FACE_MORPH_NAMES.includes(target)) continue;

            const index = child.morphTargetDictionary[target];
            if (
              index !== undefined &&
              child.morphTargetInfluences?.[index] !== undefined
            ) {
              child.morphTargetInfluences[index] = THREE.MathUtils.lerp(
                child.morphTargetInfluences[index],
                0,
                1
              );
            }
          }
        }
      });
    }
  }, [client?.isTalking, enabled, scene]);
};
